import { useDispatch } from "react-redux";
import { setLayoutDashboardTitle } from "../../../../store/ui-slice";
import { useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import UiContext from "../../../../store/ui-context";
import { ProgressSpinner } from "primereact/progressspinner";
import { useReactToPrint } from "react-to-print";
import HeaderTemplate from "./report-template-header";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useAPIGetReportProductCustomerSummary } from "../api/api-get-report-product-customer-summary";
import ReportTemplateGroup from "./report-template-group";
import ReportTemplateFilter from "./report-template-filter";
import { copyToClipboard, exportToExcel } from "./report-template-copy";

const tableWidth = "w-[80rem]";
// printContent adds p-3 padding + a border on top of the table's own width, so the
// scroll container needs a little headroom over tableWidth or a scrollbar appears
// even when nothing is actually overflowing
const contentWidth = "w-[82rem]";
const title = "PRODUCT CUSTOMER SUMMARY";

const ReportProductCustomerSummaryMain = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const uiContext = useContext(UiContext);
  const dispatch = useDispatch();

  // hides the paginator via React (not just print CSS) so it's guaranteed
  // to never appear in the printed output regardless of stylesheet timing
  const [isPrinting, setIsPrinting] = useState(false);

  const reactToPrintFn = useReactToPrint({
    contentRef,
    onBeforePrint: () =>
      new Promise((resolve) => {
        flushSync(() => setIsPrinting(true));
        resolve();
      }),
    onAfterPrint: () => setIsPrinting(false),
    onPrintError: () => {
      setIsPrinting(false);
      uiContext.showErrorMsg("Error", "Gagal mencetak laporan");
    },
  });

  const [headerValue, setHeaderValue] = useState({});
  const [printData, setPrintData] = useState([]);
  const {
    loading,
    listData,
    fetchListData,
    fetchAllData,
    filterValue,
    setFilterValue,
    paging,
    handleOnPage,
  } = useAPIGetReportProductCustomerSummary();

  // use effect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title }));
  }, []);

  // UI handler section
  const handleGenerateReport = () => {
    setHeaderValue(filterValue);
    fetchListData({ page: 1, take: paging.take });
  };

  const handleCopyToClipboard = async () => {
    const fullData = await fetchAllData();
    if (!fullData || fullData.length === 0) {
      uiContext.showErrorMsg("Error", "Tidak ada data yang dapat di-copy");
      return;
    }
    copyToClipboard({ listData: fullData, filterValue, uiContext });
  };

  const handleExportExcel = async () => {
    if (!listData || listData.length === 0) {
      uiContext.showErrorMsg("Error", "Silakan generate laporan terlebih dahulu");
      return;
    }

    const fullData = await fetchAllData();
    if (!fullData || fullData.length === 0) {
      uiContext.showErrorMsg("Error", "Tidak ada data yang dapat di-export");
      return;
    }
    exportToExcel({ listData: fullData, filterValue, uiContext });
  };

  const handlePrint = async () => {
    if (!listData || listData.length === 0) {
      uiContext.showErrorMsg("Error", "Silakan generate laporan terlebih dahulu");
      return;
    }

    const fullData = await fetchAllData();
    if (!fullData || fullData.length === 0) {
      uiContext.showErrorMsg("Error", "Tidak ada data yang dapat di-print");
      return;
    }

    setPrintData(fullData);
    reactToPrintFn();
  };

  const handleOnClickProduct = (row) => {
    const params = new URLSearchParams({
      product_id: row.product_id,
      start_date: format(filterValue.start_date ?? new Date(), "yyyy-MM-dd"),
      end_date: format(filterValue.end_date ?? new Date(), "yyyy-MM-dd"),
    });

    window.open(`/report/product-customer-list-report?${params.toString()}`, "_blank");
  };

  const handleOnClickBack = () => {
    const accept = async () => {
      navigate("..");
    };

    const reject = () => { };

    confirmDialog({
      message: `Menutup laporan ini, lanjutkan?`,
      header: "Konfirmasi ",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      acceptLabel: "Konfirmasi",
      rejectLabel: "Tidak",
      acceptClassName: "p-button-danger",
      accept,
      reject,
    });
  };

  return (
    <div className={`col-start-start p-4 gap-4 w-100`}>
      <style type="text/css" media="print">
        {`@page { size: landscape; }`}
      </style>
      <ConfirmDialog />

      {/* report header section */}
      <div className="elevated-card-tight-vertical !px-4 w-100">
        <ReportTemplateFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={handleGenerateReport}
        />
      </div>

      {/* report control section */}
      <div className="elevated-card-tight-vertical row-between-center w-full">
        <div className="row-center-center gap-6">
          <Button
            className="max-h-[2rem] min-w-[2rem] bg-red-300 border-red-300"
            icon="pi pi-arrow-left"
            severity="danger"
            onClick={handleOnClickBack}
          />
        </div>
        <div className="row-center-center gap-3">
          <Button
            icon="pi pi-copy"
            className="p-button-outlined"
            label="COPY"
            disabled={loading}
            onClick={() => handleCopyToClipboard()}
          />
          <Button
            icon="pi pi-file-excel"
            className="p-button-outlined"
            label="EXCEL"
            disabled={loading}
            onClick={handleExportExcel}
          />
          <Button
            icon="pi pi-print"
            className="p-button-outlined"
            label="PRINT"
            disabled={loading}
            onClick={handlePrint}
          />
        </div>
      </div>

      {/* report content section  */}

      <div className={`col-start-start ${contentWidth} max-w-full mx-auto overflow-x-auto`}>
        {loading && (
          <div className="fixed inset-0 z-50 row-center-center bg-black/20">
            <ProgressSpinner />
          </div>
        )}
        {!loading && (
          <div
            ref={contentRef}
            className={`printContent col-start-start bg-white shadow-md print:shadow-none p-3 border print:border-0 border-zinc-300 mx-auto`}
          >
            <HeaderTemplate
              headerValue={headerValue}
              tableWidth={tableWidth}
              title={title}
            />

            <ReportTemplateGroup
              list={isPrinting ? printData : listData}
              onProductClick={!isPrinting ? handleOnClickProduct : undefined}
              pagination={
                !isPrinting && paging.total > 0
                  ? {
                    first: (paging.page - 1) * paging.take,
                    rows: paging.take,
                    totalRecords: paging.total,
                    rowsPerPageOptions: [10, 20, 50, 100, 200, 500],
                    onPageChange: handleOnPage,
                  }
                  : null
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
export default ReportProductCustomerSummaryMain;
